<template>
  <v-dialog v-model="visible" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card class="declarations-page">
      <div class="glass-header-eracun">
        <div class="header-pattern"></div>
        <div class="header-title">
          <v-icon color="white" class="mr-2">mdi-file-document-outline</v-icon>
          <h2 class="headline-eracun white--text">Deklaracija — {{ item ? item.name : '' }}</h2>
        </div>
        <v-btn icon class="close-btn" @click="close"><v-icon color="white">mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="pt-5">
        <v-row class="justify-end pr-3 pb-2">
          <v-btn text class="page-nav-btn-header mr-2" height="38" @click="openAddOverrideField()">
            <v-icon left small>mdi-plus</v-icon>Dodaj polje samo za ovaj artikl
          </v-btn>
          <v-btn class="search-btn mr-2" height="38" @click="openPrintModal">
            <v-icon left small>mdi-printer</v-icon>Printaj
          </v-btn>
          <v-btn class="page-nav-btn-header mr-2" height="38" @click="printToPos">
            <v-icon left small>mdi-printer-pos</v-icon>POS printer
          </v-btn>
          <v-btn class="page-nav-btn-header" height="38" @click="openLabelModal">
            <v-icon left small>mdi-tag-outline</v-icon>Naljepnice
          </v-btn>
        </v-row>
        <v-row>
          <v-col cols="6" v-for="col in [1, 2]" :key="col">
            <div
              v-for="field in visibleFields(col)"
              :key="field.key"
              class="declaration-row"
              :class="{ 'declaration-row--removed': field.removed }"
            >
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="row-label">{{ field.label }}<span v-if="field.removed" class="row-meta"> (uklonjeno za ovaj artikl)</span></div>
                  <template v-if="!field.removed">
                    <div v-if="field.type === 'attribute'" class="row-value">{{ resolveAttributeValue(item, field.attribute_field) }}</div>
                    <v-text-field
                      v-else
                      v-model="field.value"
                      dense
                      outlined
                      hide-details
                      class="filter-select mt-1"
                      :placeholder="field.default_value ? `Zadano: ${field.default_value}` : ''"
                    ></v-text-field>
                  </template>
                </div>
                <v-menu>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="field.source === 'template' && !field.removed" @click="removeForThisItem(field)">
                      <v-list-item-title>Ukloni samo za ovaj artikl</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="field.source === 'override'" @click="resetOverride(field)">
                      <v-list-item-title>Poništi override</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="d-flex justify-center pb-5">
        <v-btn class="save-btn" height="45" :loading="saving" :disabled="saving" @click="save">
          Spremi
        </v-btn>
      </v-card-actions>
    </v-card>
    <add-declaration-field ref="addField" @save="onOverrideFieldSaved"></add-declaration-field>
    <declaration-print-settings-modal ref="printSettingsModal" @saved="onPrintSettingsSaved"></declaration-print-settings-modal>
    <declaration-label-settings-modal ref="labelSettingsModal" @saved="onLabelSettingsSaved"></declaration-label-settings-modal>
  </v-dialog>
</template>
<script>
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import { v4 as uuidv4 } from 'uuid'
import state from '@/state'
import { resolveAttributeValue, hasMeaningfulData } from './declarationsService.js'
import AddDeclarationField from './AddDeclarationField.vue'
import DeclarationPrintSettingsModal from './DeclarationPrintSettingsModal.vue'
import DeclarationLabelSettingsModal from './DeclarationLabelSettingsModal.vue'
import { printDeclarationToPos } from '@/libs/bixolon/declaration.js'

export default {
  name: 'EditDeclaration',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  components: { AddDeclarationField, DeclarationPrintSettingsModal, DeclarationLabelSettingsModal },
  data: () => ({
    visible: false,
    saving: false,
    item: null,
    declarationId: null,
    active: true,
    fields: [],
    template: { fields: [] }
  }),
  methods: {
    resolveAttributeValue,
    open (item, declaration, template) {
      this.item = item
      this.declarationId = item.id
      this.active = declaration.active
      this.fields = clone(declaration.fields || [])
      this.template = template
      this.visible = true
    },
    close () {
      this.visible = false
    },
    visibleFields (column) {
      return this.fields
        .filter(f => f.column === column)
        .sort((a, b) => a.order - b.order)
    },
    removeForThisItem (field) {
      const idx = this.fields.findIndex(f => f.key === field.key)
      this.fields.splice(idx, 1, { ...this.fields[idx], source: 'override', removed: true })
    },
    resetOverride (field) {
      const templateField = this.template.fields.find(tf => tf.key === field.key)
      const idx = this.fields.findIndex(f => f.key === field.key)
      if (templateField) {
        this.fields.splice(idx, 1, { ...templateField, source: 'template' })
      } else {
        this.fields.splice(idx, 1)
      }
    },
    openAddOverrideField () {
      this.$refs.addField.open()
    },
    onOverrideFieldSaved (fieldData) {
      const order = this.visibleFields(fieldData.column).length
      this.fields.push({ key: uuidv4(), order, source: 'override', ...fieldData })
    },
    async save () {
      this.saving = true
      try {
        if (hasMeaningfulData(this.fields)) {
          await df.doc(`declarations/${this.declarationId}`).set({
            company_id: state.getCurrentCompany().id,
            item_id: this.item.id,
            item_name: this.item.name,
            item_code: this.item.code,
            active: this.active,
            fields: this.fields
          })
        }
        this.$emit('success')
        this.visible = false
      } catch (err) {
        this.showMsgBox({
          text: err && err.message ? err.message : 'Greška prilikom spremanja deklaracije',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
      this.saving = false
    },
    openPrintModal () {
      this.$refs.printSettingsModal.open('single', this.template.catalog_print_settings, { item: this.item, fields: this.fields })
    },
    onPrintSettingsSaved (settings) {
      this.template.catalog_print_settings = settings
      this.$emit('settings-updated', settings)
    },
    openLabelModal () {
      this.$refs.labelSettingsModal.open('single', this.template.label_print_settings, { item: this.item, fields: this.fields })
    },
    onLabelSettingsSaved (settings) {
      this.template.label_print_settings = settings
    },
    async printToPos () {
      const printerConfiguration = state.getPrinterData()
      if (!printerConfiguration || !printerConfiguration.library) {
        this.showMsgBox({
          text: 'Printer nije konfiguriran.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        return
      }
      this.showLoader()
      const result = await printDeclarationToPos(this.item, this.fields, printerConfiguration)
      this.hideLoader()
      if (!result.success) {
        this.showMsgBox({
          text: result.error || 'Greška prilikom printanja na POS printer',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    }
  }
}
</script>
<style scoped>
.declarations-page {
  background: #F5F7FA;
}
.glass-header-eracun {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, rgba(0, 179, 190, 0.5) 0%, rgba(57, 137, 201, 0.45) 50%, rgba(97, 84, 164, 0.5) 100%) !important;
  position: relative !important;
}
.header-pattern {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}
.header-title {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}
.headline-eracun {
  font-size: 20px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  margin: 0 !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.close-btn {
  background: rgba(255, 255, 255, 0.15);
  position: relative;
  z-index: 1;
}
.declaration-row {
  border: 1px solid #EEF2F5;
  border-left: 3px solid #E0E0E0;
  border-radius: 6px;
  background: #fff;
  padding: 10px;
  margin-bottom: 8px;
  transition: border-color 0.15s, background 0.15s;
}
.declaration-row:hover {
  border-color: #00BCD4;
  background: #F8FAFB;
}
.declaration-row--removed {
  opacity: 0.55;
  border-left-color: #CFD8DC;
}
.row-label {
  font-weight: 600;
  color: #263238;
}
.row-value {
  color: #37474F;
  margin-top: 2px;
}
.row-meta {
  font-size: 12px;
  color: #94A3B8;
  font-weight: 400;
}
.filter-select >>> fieldset {
  border-color: #B0BEC5;
  border-width: 1.5px;
  border-radius: 8px;
}
.filter-select.v-input--is-focused >>> fieldset {
  border-color: #00838F;
  border-width: 2px;
}
.search-btn {
  background: rgba(0, 188, 212, 0.08);
  color: #00838F;
  border: 1.2px solid #00b3be;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  box-shadow: none;
}
.page-nav-btn-header {
  background: #fff;
  color: #3989c9;
  border: 1px solid rgba(57, 137, 201, 0.25);
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
}
.save-btn {
  background: rgba(0, 188, 212, 0.08);
  color: #00838F;
  border: 1.2px solid #00b3be;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  box-shadow: none;
}
</style>
